import { memo, useMemo, useEffect, useRef, CSSProperties } from "react";
import { NodeProps, Handle, Position, useUpdateNodeInternals, useReactFlow } from "reactflow";
import { NodeResizer } from "@reactflow/node-resizer";
import "@reactflow/node-resizer/dist/style.css";
import { CustomNodeData } from './types';
import { HandleConfig } from '../../types';
import "./CustomNode.css";
import { Box } from "lucide-react";
import { EXTENDED_ICON_MAP } from "../../config/icons";
import { useNodeResizing } from '../../hooks/useNodeResizing';
import { calculateHandleStyle, NODE_DIMENSIONS } from '../../utils/nodeHelpers';

const hexToRgba = (hex: string, alpha: number): string => {
  const validHex = typeof hex === 'string' ? hex.replace('#', '') : '';
  if (validHex.length !== 6) {
    return `rgba(0, 0, 0, 0)`;
  }
  const bigint = parseInt(validHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

function CustomNode({ data, selected, id }: NodeProps<CustomNodeData>) {
  const updateNodeInternals = useUpdateNodeInternals();
  const nodeContentRef = useRef<HTMLDivElement>(null);
  const { setNodes } = useReactFlow();

  const calculateMinHeight = useMemo(() => {
    return (width: number, headerHeight: number) => {
      let calculatedHeight = NODE_DIMENSIONS.VERTICAL_PADDING;
      calculatedHeight += headerHeight || NODE_DIMENSIONS.HEADER_CONTENT_ESTIMATED_HEIGHT;

      const showLabels = width >= NODE_DIMENSIONS.HIDE_LABELS_WIDTH_THRESHOLD;
      const labelsExist = Array.isArray(data.labels) && data.labels.length > 0 && data.labelsVisibility !== "hidden";

      if (showLabels && labelsExist) {
        calculatedHeight += NODE_DIMENSIONS.LABEL_ROW_ESTIMATED_HEIGHT;
        // Simple heuristic: if more than 1 label and width is tight, assume a second row for labels
        if (data.labels.length > 1 && width < (NODE_DIMENSIONS.MIN_WIDTH + 60)) {
          calculatedHeight += NODE_DIMENSIONS.LABEL_ROW_ESTIMATED_HEIGHT;
        }
      }
      return Math.min(calculatedHeight, NODE_DIMENSIONS.MAX_HEIGHT);
    };
  }, [data.labels, data.labelsVisibility]);

  const labels = useMemo(() => {
    return Array.isArray(data.labels) ? data.labels : [];
  }, [data.labels]);

  const isJunctionNode = useMemo(() => {
    const isNameEffectivelyHidden = !data.name || data.nameVisibility === 'hidden';
    const areLabelsEffectivelyHidden = labels.length === 0 || data.labelsVisibility === 'hidden';
    return isNameEffectivelyHidden && areLabelsEffectivelyHidden;
  }, [data.name, data.nameVisibility, data.labelsVisibility, labels]);

  const {
    onResize,
    onResizeEnd,
    currentWidth: resizerCurrentWidth,
    currentHeight: resizerCurrentHeight,
    effectiveMinHeight,
  } = useNodeResizing({
    id,
    nodeType: 'custom-node',
    minWidth: NODE_DIMENSIONS.MIN_WIDTH,
    maxWidth: NODE_DIMENSIONS.MAX_WIDTH,
    minHeight: NODE_DIMENSIONS.MIN_HEIGHT,
    maxHeight: NODE_DIMENSIONS.MAX_HEIGHT,
    contentRef: nodeContentRef,
    calculateMinHeight,
    autoAdjustHeight: !isJunctionNode, // Disable auto-adjust for junction nodes
    lockHeight: isJunctionNode, // Lock height for junction nodes
  });

  // Determine the actual width and height to use for the node wrapper
  const actualWidth = isJunctionNode ? 20 : resizerCurrentWidth;
  const actualHeight = isJunctionNode ? 20 : resizerCurrentHeight;

  // Effect to set fixed dimensions for junction nodes in React Flow state
  const prevIsJunctionNode = useRef(isJunctionNode);

  useEffect(() => {
    // Case 1: Transition from non-junction to junction
    if (isJunctionNode && !prevIsJunctionNode.current) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? {
                ...n,
                width: 20,
                height: 20,
              }
            : n
        )
      );
      updateNodeInternals(id); // Ensure handles update
    }
    // Case 2: Transition from junction to non-junction
    else if (!isJunctionNode && prevIsJunctionNode.current) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? {
                ...n,
                width: 128, // Set to 128px as requested
                height: NODE_DIMENSIONS.MIN_HEIGHT, // Or a reasonable default height
              }
            : n
        )
      );
      updateNodeInternals(id); // Ensure handles update
    }

    prevIsJunctionNode.current = isJunctionNode;
  }, [id, isJunctionNode, setNodes, updateNodeInternals]);

  const IconComponent = useMemo(() => {
    return EXTENDED_ICON_MAP[data.icon] || Box;
  }, [data.icon]);

  const handles = useMemo(() => {
    return Array.isArray(data.handles) ? [...data.handles] : [];
  }, [data.handles]);

  const handlesByPosition = useMemo(() => {
    return handles.reduce((acc, handle) => {
      if (!acc[handle.position]) {
        acc[handle.position] = [];
      }
      acc[handle.position].push(handle);
      return acc;
    }, {} as Record<Position, HandleConfig[]>);
  }, [handles]);

  const nodeClasses = `
    w-full h-full rounded-lg border transition-all shadow-sm dark:shadow-sm-dark hover:shadow-md dark:hover:shadow-md-dark overflow-hidden
    ${selected
      ? "border-blue-500 shadow-blue-100 outline outline-4 outline-blue-500/20 outline-offset-0"
      : "border-gray-300 dark:border-gray-600"
    }
    ${isJunctionNode ? '!w-5 !h-5 !p-0 !min-w-0 flex items-center justify-center' : ''}
  `;

  const nodeStyle: CSSProperties = useMemo(() => {
    const baseStyle: CSSProperties = isJunctionNode ? {} : { minWidth: NODE_DIMENSIONS.MIN_WIDTH, maxWidth: NODE_DIMENSIONS.MAX_WIDTH };
    if (data.color) {
      const opacity = data.colorOpacity ?? 1;
      baseStyle.backgroundColor = hexToRgba(data.color, opacity * 0.4);
      baseStyle.borderColor = hexToRgba(data.color, opacity * 0.5);
    } else {
    }
    return baseStyle;
  }, [data.color, data.colorOpacity, isJunctionNode]);

  return (
    <div
      style={{
        width: actualWidth,
        height: actualHeight,
        position: 'relative',
      }}
      className={`node-wrapper ${isJunctionNode ? 'no-transition' : ''}`}
    >
      {selected && !isJunctionNode && (
        <NodeResizer
          minWidth={NODE_DIMENSIONS.MIN_WIDTH}
          maxWidth={NODE_DIMENSIONS.MAX_WIDTH}
          minHeight={effectiveMinHeight}
          maxHeight={NODE_DIMENSIONS.MAX_HEIGHT}
          isVisible={selected}
          onResize={onResize}
          onResizeEnd={onResizeEnd}
          handleStyle={{
            width: '8px',
            height: '8px',
            backgroundColor: '#3b82f6',
            border: '1px solid white',
            borderRadius: '2px',
          }}
          lineStyle={{
            borderColor: '#3b82f6',
            borderWidth: '1px',
          }}
        />
      )}

      <div
        className={`${nodeClasses} ${!data.color ? 'bg-white dark:bg-gray-800' : ''}`}
        style={{
          ...nodeStyle,
          width: isJunctionNode ? 'auto' : '100%',
          height: '100%',
        }}
      >
          {isJunctionNode ? (
            <>
              {data.iconVisibility !== "hidden" && (
                <div className="flex items-center justify-center w-full h-full text-gray-600 dark:text-gray-300">
                  <IconComponent size={12} />
                </div>
              )}
            </>
          ) : (
            <div ref={nodeContentRef} className="flex flex-col justify-center items-center px-3 py-2 w-full h-full">
              <div className="flex items-center pr-1">
                {data.iconVisibility !== "hidden" && (
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mr-1 text-gray-600 dark:text-gray-300">
                    <IconComponent size={18} />
                  </div>
                )}
                {data.nameVisibility !== "hidden" && data.name && (
                  <h3
                    className="font-medium text-sm text-gray-900 dark:text-gray-100 flex-grow truncate"
                    title={data.name}
                  >
                    {data.name}
                  </h3>
                )}
              </div>

              {actualWidth >= NODE_DIMENSIONS.HIDE_LABELS_WIDTH_THRESHOLD && data.labelsVisibility !== "hidden" && labels.length > 0 && (
                <div className="custom-node-labels-container pt-1 pb-0 truncate">
                  {labels.map((label, index) => (
                    <span
                      key={index}
                      className="custom-node-label-chip truncate max-w-full"
                      title={label}
                    >
                      {label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {Object.entries(handlesByPosition).map(([_, positionHandles]) =>
            positionHandles.map((handle, index) => (
              <Handle
                key={`${handle.id}-${handle.color}`}
                id={handle.id}
                type={handle.type}
                position={handle.position as Position}
                className={`!absolute !w-2.5 !h-2.5 !border !border-white dark:!border-gray-700`}
                style={{
                  ...calculateHandleStyle(index, positionHandles.length, handle.position),
                  backgroundColor: handle.color,
                }}
              />
            ))
          )}
        </div>
    </div>
  );
}

export default memo(CustomNode);
